import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import {
  Dropdown, DropdownItem, DropdownMenu, DropdownToggle,
} from 'reactstrap';

import LocaleService from '@services/LocaleService';
import { getInitials } from '@shared/helpers';
import { CheckIcon, ChevronDownIcon, UsersIcon } from '@shared/icons';

export type GuestDentist = {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  specialties: string[];
  image_url?: string | null;
};

type Props = {
  clinicName: string;
  dentists: GuestDentist[];
  value: string | null;
  onChange: (dentistId: string | null) => void;
  error?: string;
};

const i18n = LocaleService.getTranslations('referrals.guestRefer.preferredDentist') as any;

const Avatar = ({ dentist }: { dentist?: GuestDentist }) => {
  if (!dentist) {
    return (
      <span className="guest-refer-dentist-avatar guest-refer-dentist-avatar-any">
        <UsersIcon />
      </span>
    );
  }
  return dentist.image_url ? (
    <img className="guest-refer-dentist-avatar" src={dentist.image_url} alt="" />
  ) : (
    <span className="guest-refer-dentist-avatar">{getInitials(dentist.first_name, dentist.last_name)}</span>
  );
};

const Label = ({ title, hint }: { title: string; hint?: string }) => (
  <span className="guest-refer-dentist-text">
    <span className="guest-refer-dentist-title">{title}</span>
    {hint && <span className="guest-refer-dentist-hint">{hint}</span>}
  </span>
);

/**
 * ENH-056 (client design, 2026-10-29): beside the clinic in "Your referral goes
 * to", the guest may pick one of the clinic's dentists. "Any available dentist"
 * (the default) sends to the clinic, as before.
 */
const GuestPreferredDentist = ({
  clinicName, dentists, value, onChange, error,
}: Props) => {
  const [ open, setOpen ] = useState(false);
  const [ width, setWidth ] = useState<number>();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const picked = dentists.find((dentist) => dentist.id === value);

  // The menu sits in <body>, outside the card that clips it, so it is given
  // the field's width.
  const toggle = () => {
    setWidth(toggleRef.current?.offsetWidth);
    setOpen((current) => !current);
  };

  const option = (dentist: GuestDentist | undefined) => {
    const selected = (dentist?.id ?? null) === value;
    return (
      <DropdownItem
        key={dentist?.id ?? 'any'}
        className={classNames('guest-refer-dentist-option', { 'is-selected': selected })}
        aria-selected={selected}
        onClick={() => onChange(dentist?.id ?? null)}
      >
        <Avatar dentist={dentist} />
        {dentist
          ? <Label title={dentist.full_name} hint={dentist.specialties.join(', ')} />
          : <Label title={i18n.any} hint={i18n.anyOptionHint} />}
        {selected && <CheckIcon className="guest-refer-dentist-check" width={14} height={10} />}
      </DropdownItem>
    );
  };

  return (
    <div className="guest-refer-dentist">
      <div className="guest-refer-dentist-label" id="guest-refer-dentist-label">
        {i18n.label} <span className="guest-refer-dentist-optional">{i18n.optional}</span>
      </div>
      <Dropdown isOpen={open} toggle={toggle} menuRole="listbox">
        <DropdownToggle
          tag="button"
          type="button"
          innerRef={toggleRef}
          id="guest-refer-dentist"
          className={classNames('guest-refer-dentist-toggle', { 'is-open': open, 'is-invalid': !!error })}
          aria-labelledby="guest-refer-dentist-label guest-refer-dentist"
        >
          <Avatar dentist={picked} />
          {picked
            ? <Label title={picked.full_name} hint={picked.specialties.join(', ')} />
            : <Label title={i18n.any} hint={LocaleService.parseTranslation(i18n.anyHint, { ':clinic': clinicName })} />}
          <ChevronDownIcon className="guest-refer-dentist-caret" />
        </DropdownToggle>
        <DropdownMenu container="body" className="guest-refer-dentist-menu" style={{ width }}>
          {option(undefined)}
          <DropdownItem divider />
          {dentists.map((dentist) => option(dentist))}
        </DropdownMenu>
      </Dropdown>
      {error && <div className="guest-refer-dentist-error">{error}</div>}
    </div>
  );
};

GuestPreferredDentist.defaultProps = {
  error: '',
};

export default GuestPreferredDentist;
